import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Col, Label, Row } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import {
  clearStripeValidationMessage,
  getFeesSettings,
  getStripeClientSecret,
  register,
} from '../RegisterActions';
import FormInputField from '../../common/components/formFields/FormInputField';
import CommonErrorLabel from '../../common/components/CommonErrorLabel';
import { DealerRole } from '../../constants/DealerRole';
import CommonButton from '../../common/components/CommonButton';
import { getCardElementOptions } from '../../constants/CardElementOptions';
import { validateFormField } from '../../common/helpers/validateFormField';
import { FormFieldTypes } from '../../constants/FormFieldTypes';
import { pencesToPounds } from '../../common/helpers/pencesToPounds';

import creditCardIcon from '../../assets/img/icons/credit_card.svg';
import keyIcon from '../../assets/img/icons/key_stripe.svg';
import calendarIcon from '../../assets/img/icons/calendar_month.svg';
import { scrollTop } from '../../common/helpers/generalHelper';

const PROMO_CODE_MAX_LENGTH = 100;

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);

  const handleResize = useCallback(() => setWidth(window.innerWidth), []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  return width;
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function AddPaymentDetailsFormInner({ values, nextStep, prevStep }) {
  const feesSettings = useSelector((state) => state.register.get('feesSettings'));

  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const width = useWindowWidth();
  const [submitted, setSubmitted] = useState(false);
  const isMobile = useMemo(() => width < 768, [width]);
  useEffect(() => {
    dispatch(getFeesSettings());
    scrollTop();
  }, [dispatch]);

  const [isOn, setSwitch] = useState(true);

  const isFranchisedDealer = useMemo(
    () => values?.organizationRole === DealerRole.FRANCHISED_DEALER,
    [values?.organizationRole],
  );
  const isLoading = useSelector((state) => state.navbarLoader.get('isLoading'));

  const stripeValidationMessage = useSelector((state) =>
    state.register.get('stripeValidationMessage'),
  );

  const [errors, setErrors] = useState({
    cardholderError: '',
    cvcCodeError: '',
    cardNumberError: '',
    expiryError: '',
    promoCodeError: '',
  });

  const hasAnyError = useCallback(() => {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in errors) {
      if (errors[key]) {
        return true;
      }
    }

    return (
      isOn && !values?.cardholder && values?.promoCode?.trim()?.length > PROMO_CODE_MAX_LENGTH
    );
  }, [errors, isOn, values?.cardholder, values?.promoCode]);

  const handleInputChange = useCallback((name, error) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`${name}Error`]: error ? error.message : '',
    }));
  }, []);

  const isSubmittingDisabled = useMemo(
    () => isLoading || (isOn && hasAnyError()),
    [isLoading, isOn, hasAnyError],
  );

  const validateForm = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const { cardholder, promoCode } = values;

        const cardholderError = validateFormField(cardholder, FormFieldTypes.STRING, true);

        let promoCodeError;

        if (promoCode?.trim()?.length > PROMO_CODE_MAX_LENGTH) {
          promoCodeError = `Max length equals ${PROMO_CODE_MAX_LENGTH} characters`;
        }

        const formErrors = {
          ...errors,
          cardholderError,
          promoCodeError,
        };

        setErrors(formErrors);

        if (Object.values(formErrors).some((error) => error)) {
          reject(formErrors);
        } else {
          resolve();
        }
      }),
    [values, errors, setErrors],
  );

  useEffect(() => {
    if (submitted) {
      validateForm().catch(() => {});
    }
  }, [values, submitted, validateForm]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitted(true);
      if (!stripe || !elements) {
        return;
      }
      try {
        await validateForm().then(() => {
          dispatch(
            getStripeClientSecret(values, stripe, elements, (stripeCustomerId) =>
              dispatch(register(values, () => nextStep(), stripeCustomerId)),
            ),
          );
        });
      } catch (errs) {
        if (isMobile) {
          const firstErrorField = Object.keys(errs).find((key) => errs[key]);
          if (firstErrorField) {
            const fieldName = firstErrorField.replace('Error', '');
            const element = document.getElementsByName(fieldName)[0];
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      }
    },
    [
      stripe,
      elements,
      isFranchisedDealer,
      isOn,
      dispatch,
      values,
      validateForm,
      nextStep,
      isMobile,
    ],
  );

  const handleSwitchToggle = useCallback(() => setSwitch(!isOn), [isOn]);

  const cardElementOptions = useMemo(() => getCardElementOptions(!isOn), [isOn]);

  const sellersFee = useMemo(
    () =>
      feesSettings.saleBetween1001And5000InPence ===
        feesSettings.saleBetween5001And10000InPence &&
      feesSettings.saleBetween5001And10000InPence ===
        feesSettings.saleBetween10001And15000InPence &&
      feesSettings.saleBetween10001And15000InPence ===
        feesSettings.saleForLessThan1000InPence &&
      feesSettings.saleForLessThan1000InPence === feesSettings.saleForMoreThan15000InPence
        ? feesSettings.saleBetween5001And10000InPence
        : 4500,
    [feesSettings],
  );
  return (
    <Col className="register-form-container d-flex flex-column justify-content-center align-items-center">
      <Row md={12} className="register-form-container__title w-100">
        <Col md={12}>
          <h2 className="font-weight-bold">Subscription details</h2>
          <p className="subscription-description">
            <span className="subscription-highlight">
              No subscription fee will be charged.
            </span>
          </p>
        </Col>
      </Row>
      <Row className="register-form-container__content w-100 mt-3">
        <div className="register-form__payment-details">
          <Row className="mb-3">
            <Col md="6">
              <div className="p-1 mobile-margin">
                <FormInputField
                  name="cardholder"
                  id="cardholder"
                  placeholder=""
                  label="Card Holder Name"
                  labelVariant="bordered"
                  disabled={!isOn}
                  required={isOn}
                />
                {errors.cardholderError && <CommonErrorLabel value={errors.cardholderError} />}
              </div>
            </Col>
            <Col md="6">
              <div className="p-1">
                <div
                  className={`form-group--bordered-label ${
                    !isOn ? 'form-group--disabled' : ''
                  }`}
                >
                  <Label
                    className={`form-input-field-label ${
                      !isOn ? 'form-input-field-label__disabled' : 'label-required'
                    }`}
                  >
                    Credit Card Number
                  </Label>
                  <div
                    className={`form-control card-element-wrapper mb-2 position-relative ${
                      !isOn ? 'form-control--disabled' : ''
                    }`}
                  >
                    <CardNumberElement
                      elements={elements}
                      options={cardElementOptions}
                      onChange={({ error }) => handleInputChange('cardNumber', error)}
                      id="credit-card-container"
                    />
                    <img
                      src={creditCardIcon}
                      className="input-icon input-right-icon"
                      alt="card number"
                    />
                  </div>
                </div>
                {stripeValidationMessage ? (
                  <CommonErrorLabel value={stripeValidationMessage} />
                ) : (
                  ''
                )}
                <CommonErrorLabel value={errors?.cardNumberError || ''} />
              </div>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md="6" className="mobile-margin">
              <div className="p-1">
                <div
                  className={`form-group--bordered-label ${
                    !isOn ? 'form-group--disabled' : ''
                  }`}
                >
                  <Label
                    className={`form-input-field-label ${
                      !isOn ? 'form-input-field-label__disabled' : 'label-required'
                    }`}
                  >
                    Expiration Date
                  </Label>
                  <div
                    className={`form-control card-element-wrapper mb-2 position-relative ${
                      !isOn ? 'form-control--disabled' : ''
                    }`}
                  >
                    <CardExpiryElement
                      elements={elements}
                      options={cardElementOptions}
                      onChange={({ error }) => handleInputChange('expiry', error)}
                      id="credit-card-container"
                    />
                    <img
                      src={calendarIcon}
                      className="input-icon input-right-icon"
                      alt="card exp date"
                    />
                  </div>
                </div>
                <CommonErrorLabel value={errors?.expiryError || ''} />
              </div>
            </Col>
            <Col md="6">
              <div className="p-1 mobile-margin">
                <div
                  className={`form-group--bordered-label  ${
                    !isOn ? 'form-group--disabled' : ''
                  }`}
                >
                  <Label
                    className={`form-input-field-label ${
                      !isOn ? 'form-input-field-label__disabled' : 'label-required'
                    }`}
                  >
                    CVC Code
                  </Label>
                  <div
                    className={`form-control card-element-wrapper mb-2 position-relative ${
                      !isOn ? 'form-control--disabled' : ''
                    }`}
                  >
                    <CardCvcElement
                      elements={elements}
                      options={cardElementOptions}
                      onChange={({ error }) => handleInputChange('cvcCode', error)}
                      id="credit-card-container"
                    />
                    <img
                      src={keyIcon}
                      className="input-icon input-right-icon"
                      alt="card cvc number"
                    />
                  </div>
                </div>
                <CommonErrorLabel value={errors?.cvcCodeError || ''} />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <div className="p-1 mobile-margin">
                <FormInputField
                  name="promoCode"
                  id="promoCode"
                  placeholder=""
                  label="Promo Code"
                  labelVariant="bordered"
                />
                {errors.promoCodeError && <CommonErrorLabel value={errors.promoCodeError} />}
              </div>
            </Col>
          </Row>
          <div className="register-form-container__title w-100 margin-top-2 dekstop-margin-top w-100">
            <Col md={12} className="w-100">
              <h2
                style={{
                  paddingBottom: '16px',
                  marginBottom: '16px',
                  width: '100%',
                  borderBottom: '2px solid #F1001E',
                }}
              >
                Fees info
              </h2>
            </Col>
          </div>
          <Row className="payment-details__table">
            <Col md="6" className="mt-2">
              <p className="fees-table-title">
                <strong>Buyers Fees:</strong>
              </p>
              <div className="fees-table-container">
                <p className="fees-table-description">
                  See below buyers fees that apply to each vehicle purchase:
                </p>
                <table className="price-table">
                  <tbody>
                    <tr>
                      <td>
                        <p className="price__cell-text">£0 - £1,000</p>
                      </td>
                      <td>
                        <p className="price__cell-text">
                          <strong>
                            £{pencesToPounds(feesSettings.purchaseForLessThan1000InPence)} +
                            VAT
                          </strong>
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p className="price__cell-text">£1,001 - £5,000</p>
                      </td>
                      <td>
                        <p className="price__cell-text">
                          <strong>
                            £{pencesToPounds(feesSettings.purchaseBetween1001And5000InPence)} +
                            VAT
                          </strong>
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p className="price__cell-text">£5,001 - £10,000</p>
                      </td>
                      <td>
                        <p className="price__cell-text">
                          <strong>
                            £{pencesToPounds(feesSettings.purchaseBetween5001And10000InPence)}{' '}
                            + VAT
                          </strong>
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p className="price__cell-text">£10,001 - £15,000</p>
                      </td>
                      <td>
                        <p className="price__cell-text">
                          <strong>
                            £{pencesToPounds(feesSettings.purchaseBetween10001And15000InPence)}{' '}
                            + VAT
                          </strong>
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p className="price__cell-text">£15,001+</p>
                      </td>
                      <td>
                        <p className="price__cell-text">
                          <strong>
                            £{pencesToPounds(feesSettings.purchaseForMoreThan15000InPence)} +
                            VAT
                          </strong>
                        </p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Col>
            <Col md="6" className="mt-2">
              <p className="fees-table-title">
                <strong>Sellers Fees:</strong>
              </p>
              <div className="sellers-container">
                <p className="sellers-description">
                  The Stock Vault set out to make selling your vehicles quick and easy.
                  That&apos;s why we&apos;ve built a streamlined platform, ready to use.
                </p>
                <p className="sellers-fee-text">
                  {`Sellers pay a one-off £${pencesToPounds(sellersFee)} per listing fee.`}{' '}
                  <br /> No hidden costs.
                </p>
              </div>
            </Col>
          </Row>
        </div>
      </Row>
      <Row className="justify-content-center w-100 mb-3" style={{ marginTop: '32px' }}>
        <Col md="6" xs="6">
          <CommonButton
            label="PREVIOUS"
            variant={CommonButtonVariants.OUTLINED_RED}
            handleClick={prevStep}
            className="w-100"
          />
        </Col>
        <Col md="6" xs="6">
          <CommonButton
            variant={CommonButtonVariants.PRIMARY_RED}
            handleClick={handleSubmit}
            disabled={isSubmittingDisabled}
            className="w-100 register-account-button"
            label={isMobile ? 'Register' : 'Confirm & Register Account'}
          />
        </Col>
        {isFranchisedDealer && (
          <Col md="12" className="text-center mt-3">
            <CommonButton
              variant={CommonButtonVariants.OUTLINED}
              onClick={handleSwitchToggle}
              className="w-100 skip-payment-button"
            >
              {isOn ? 'Skip Payment Details' : 'Add Payment Details'}
            </CommonButton>
          </Col>
        )}
      </Row>
    </Col>
  );
}

function AddPaymentDetailsForm({ values, nextStep, prevStep }) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearStripeValidationMessage());
  }, [dispatch]);

  return (
    <Elements stripe={stripePromise}>
      <AddPaymentDetailsFormInner nextStep={nextStep} prevStep={prevStep} values={values} />
    </Elements>
  );
}

AddPaymentDetailsForm.propTypes = {
  prevStep: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
};

export default AddPaymentDetailsForm;
